import { Button, InlineNotification } from '@carbon/react';
import {
  describeSurveyAreaCandidate,
  type SurveyAreaPick,
  type SurveyRowsFromUnit,
} from 'components/survey-description';
import styles from './create-area-panel.module.scss';

interface SurveyPickNoticesProps {
  surveyRowsFromUnit: SurveyRowsFromUnit;
  surveyAreaPick: SurveyAreaPick;
}

/**
 * What the survey method's map picks have to say: a unit with nothing to copy, a
 * section that couldn't be added, or an overlap to choose from by name.
 *
 * @param {SurveyPickNoticesProps} props - The two map picks.
 * @return {JSX.Element} The notices that apply.
 */
export function SurveyPickNotices({
  surveyRowsFromUnit,
  surveyAreaPick,
}: SurveyPickNoticesProps): JSX.Element {
  return (
    <>
      {surveyRowsFromUnit.notice && (
        <InlineNotification
          kind="info"
          lowContrast
          title="Nothing to copy"
          subtitle={surveyRowsFromUnit.notice}
          onCloseButtonClick={surveyRowsFromUnit.dismiss}
        />
      )}

      {surveyAreaPick.notice && (
        <InlineNotification
          kind="error"
          lowContrast
          title="Could not add that section"
          subtitle={surveyAreaPick.notice}
          onCloseButtonClick={surveyAreaPick.dismiss}
        />
      )}

      {/*
        An overlap the map can't decide — different shapes at the same point.
        The choice is made here, on the names, rather than sending the user off
        to search for one of them by hand.
      */}
      {surveyAreaPick.choices.length > 0 && (
        <>
          <InlineNotification
            kind="info"
            lowContrast
            title="More than one survey area here"
            subtitle="Pick the one you meant:"
            onCloseButtonClick={surveyAreaPick.dismiss}
          />
          {/*
            The buttons sit beside the notification, not inside it — Carbon's
            `InlineNotification` forbids interactive children and throws on
            them in non-production builds.
          */}
          <span className={styles.pickChoices}>
            {surveyAreaPick.choices.map((candidate) => (
              <Button
                key={candidate.id}
                kind="ghost"
                size="sm"
                onClick={() => surveyAreaPick.choose(candidate)}
              >
                {/*
                  Named the way the search results are — Section vs Abstract
                  and the acreage — since the names alone can read identically.
                */}
                {describeSurveyAreaCandidate(candidate)}
              </Button>
            ))}
          </span>
        </>
      )}
    </>
  );
}
